// client/src/flow/components/AISearchBox.js
// VERSÃO COM SUPORTE PARA SGPITS E UGITS

import React, { useState, useEffect } from 'react';
import {
  Search,
  Sparkles,
  Bot,
  Send,
  Loader,
  MessageCircle,
  ChevronRight,
  X,
  AlertCircle,
  ExternalLink,
  FileText,
  Phone,
  Info
} from 'lucide-react';
import config from '../config';
import './AISearchBox.css';
import { convertWhatsAppUrl, isMobileDevice } from '../../utils/whatsappUtils';

const AISearchBox = ({ flow, currentStep, onNavigate, embedded = false, debug = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [question, setQuestion] = useState('');
  const [charCount, setCharCount] = useState(40);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [currentEmojiIndex, setCurrentEmojiIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Emojis diferentes para UGITS
  const emojis = flow?.url === 'ugits' 
    ? ['💡', '🚀', '🔬', '⚡', '🎯'] 
    : ['🔬', '📖', '💡', '🧪', '🧠'];

  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(isMobileDevice());
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setCurrentEmojiIndex((prev) => (prev + 1) % emojis.length);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [loading, emojis]);

  // Mostrar para SGPITS e UGITS
  if (flow?.url !== 'sgpits' && flow?.url !== 'ugits') return null;

  // Determinar qual endpoint usar
  const getApiEndpoint = () => {
    return flow?.url === 'ugits' 
      ? `${config.serverUrl}/api/flows/ugits/ai-assistant`
      : `${config.serverUrl}/api/flows/sgpits/ai-assistant`;
  };

  // Determinar título do assistente
  const getAssistantTitle = () => {
    return flow?.url === 'ugits'
      ? 'Assistente IA da UGITS'
      : 'Assistente IA do SGPITS';
  };

  // Exemplos de perguntas baseado no sistema
  const getExampleQuestions = () => {
    if (flow?.url === 'ugits') {
      return [
        "Como participar do PIT?",
        "Como acessar o REDCap?",
        "O que é NATS?",
        "Quais ferramentas digitais?"
      ];
    }
    return [
      "Como agendar consultoria estatística?",
      "O que é TCLE?",
      "Como acessar o REDCap?",
      "Onde encontro modelos de documentos?"
    ];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim() || loading) return;

    setLoading(true);
    setResponse(null);

    try {
      const requestBody = flow?.url === 'ugits'
        ? { question } // UGITS só precisa da pergunta
        : { 
            question,
            currentStep: currentStep?.id,
            flowStructure: flow.formConfig
          };

      const res = await fetch(getApiEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await res.json();
      
      // Converte URLs do WhatsApp
      if (data.whatsappUrl) {
        data.whatsappUrl = convertWhatsAppUrl(data.whatsappUrl);
      }
      
      if (data.additionalLinks && Array.isArray(data.additionalLinks)) {
        data.additionalLinks = data.additionalLinks.map(link => {
          if (link.whatsappUrl) {
            return {
              ...link,
              whatsappUrl: convertWhatsAppUrl(link.whatsappUrl)
            };
          }
          return link;
        });
      }
      
      if (debug) {
        console.log(`Resposta ${flow?.url?.toUpperCase()}:`, data);
        console.log('Dispositivo:', isMobile ? 'Mobile' : 'Desktop');
      }
      
      setResponse(data);
    } catch (error) {
      console.error('Erro ao processar pergunta:', error);
      
      const errorWhatsappUrl = convertWhatsAppUrl(
        `https://api.whatsapp.com/send?phone=553432182323&text=${encodeURIComponent(`Olá! Tentei usar o assistente IA mas houve um erro. Minha pergunta era: "${question}"`)}`
      );
      
      setResponse({
        error: true,
        message: 'Erro ao processar sua pergunta. Tente novamente.',
        whatsappUrl: errorWhatsappUrl,
        whatsappNote: flow?.url === 'ugits' 
          ? '📱 Observação: A UGITS atende via WhatsApp compartilhado com a UGPESQ'
          : null
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionChange = (e) => {
    const value = e.target.value;
    if (value.length <= 40) {
      setQuestion(value);
      setCharCount(40 - value.length);
    }
  };

  const handleNavigateToStep = (stepId) => {
    onNavigate(stepId);
    setShowModal(false);
    resetSearch();
  };

  const handleOpenLink = (url) => {
    const finalUrl = url.includes('whatsapp.com') ? convertWhatsAppUrl(url) : url;
    window.open(finalUrl, '_blank');
  };

  const resetSearch = () => {
    setQuestion('');
    setCharCount(40);
    setResponse(null);
  };

  const closeModal = () => {
    setShowModal(false);
    resetSearch();
  };

  const getIconForLink = (type, label) => {
    if (type === 'contact' || label?.toLowerCase().includes('whatsapp')) {
      return <MessageCircle size={20} />;
    }
    if (label?.toLowerCase().includes('acessar')) {
      return <ExternalLink size={20} />;
    }
    if (label?.toLowerCase().includes('tcle') || label?.toLowerCase().includes('modelo') || label?.toLowerCase().includes('documento')) {
      return <FileText size={20} />;
    }
    return <ChevronRight size={20} />;
  };

  const getButtonClass = (type, label) => {
    if (type === 'contact' || label?.toLowerCase().includes('whatsapp')) {
      return 'whatsapp';
    }
    if (label?.toLowerCase().includes('acessar') || label?.toLowerCase().includes('site')) {
      return 'secondary';
    }
    return 'primary';
  };

  return (
    <>
      {/* Botão flutuante */}
      <button 
        className={`ai-floating-button ${flow?.url === 'ugits' ? 'ugits-theme' : ''}`}
        onClick={() => setShowModal(true)}
        aria-label="Abrir assistente IA"
      >
        <Bot size={24} />
        <div className="ai-floating-emoji">
          ?
        </div>
      </button>

      {/* Modal */}
      {showModal && (
        <div className="ai-modal-overlay" onClick={closeModal}>
          <div className="ai-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className={`ai-modal-header ${flow?.url === 'ugits' ? 'ugits-theme' : ''}`}>
              <div className="ai-modal-title">
                <Bot size={20} />
                <span>{getAssistantTitle()}</span>
                <Sparkles size={16} className="sparkle-icon" />
              </div>
              <button onClick={closeModal} className="ai-close-btn" aria-label="Fechar">
                <X size={20} />
              </button>
            </div>

            <div className="ai-modal-body">
              {!response && !loading && (
                <form onSubmit={handleSubmit}>
                  <div className="ai-input-wrapper">
                    <input
                      type="text"
                      value={question}
                      onChange={handleQuestionChange}
                      placeholder="Faça uma pergunta simples (máx. 40 caracteres)..."
                      className="ai-input"
                      autoFocus
                      aria-label="Campo de pergunta"
                      maxLength={40}
                    />
                    <span className="ai-char-counter" aria-live="polite">
                      {charCount} restantes
                    </span>
                  </div>
                  <button 
                    type="submit" 
                    disabled={!question.trim() || loading}
                    className={`ai-submit-btn ${flow?.url === 'ugits' ? 'ugits-theme' : ''}`}
                    aria-label="Enviar pergunta"
                  >
                    {loading ? <Loader className="spinning" size={18} /> : <Send size={18} />}
                    Enviar
                  </button>
                  
                  {/* Dicas de uso */}
                  <div className="ai-tips">
                    <p className="ai-tip-title">💡 Exemplos de perguntas:</p>
                    <ul className="ai-tip-list">
                      {getExampleQuestions().map((q, i) => (
                        <li key={i}>"{q}"</li>
                      ))}
                    </ul>
                    {debug && (
                      <p className="ai-debug-device">
                        📱 Dispositivo: {isMobile ? 'Mobile' : 'Desktop'}
                      </p>
                    )}
                  </div>
                </form>
              )}

              {loading && (
                <div className="ai-loading">
                  <span className="spinning ai-loading-emoji">{emojis[currentEmojiIndex]}</span>
                  <p>Processando sua pergunta...</p>
                  <small>Buscando informações no sistema {flow?.url?.toUpperCase()}</small>
                </div>
              )}

              {response && !loading && (
                <div className="ai-response-content">
                  {/* Nota sobre WhatsApp compartilhado (apenas UGITS) */}
                  {response.whatsappNote && (
                    <div className="ai-whatsapp-note">
                      <Info size={16} />
                      <span>{response.whatsappNote}</span>
                    </div>
                  )}

                  {/* Mensagem de erro */}
                  {response.error ? (
                    <>
                      <div className="ai-error-message">
                        <AlertCircle size={24} />
                        <p>{response.message}</p>
                      </div>
                      <div className="ai-response-actions">
                        <button
                          className="ai-action-btn whatsapp"
                          onClick={() => handleOpenLink(response.whatsappUrl)}
                        >
                          <MessageCircle size={20} />
                          <div>
                            <strong>Falar com Suporte</strong>
                            <small>Obter ajuda via WhatsApp {isMobile ? '(Mobile)' : '(Web)'}</small>
                          </div>
                        </button>
                      </div>
                    </>
                  ) : response.foundInStructure ? (
                    <>
                      {/* Mensagem de sucesso */}
                      <div className="ai-found-message">
                        <Bot size={20} />
                        <div>
                          <strong>Encontrei a informação!</strong>
                          {response.confidence > 0.8 && (
                            <span className="ai-confidence high">Alta precisão</span>
                          )}
                          {response.confidence > 0.5 && response.confidence <= 0.8 && (
                            <span className="ai-confidence medium">Boa correspondência</span>
                          )}
                          <p>{response.message}</p>
                        </div>
                      </div>

                      <div className="ai-response-actions">
                        {/* Botão principal de navegação */}
                        {response.targetStep && (
                          <button
                            className="ai-action-btn primary"
                            onClick={() => handleNavigateToStep(response.targetStep)}
                          >
                            <ChevronRight size={20} />
                            <div>
                              <strong>Ir para {response.targetLabel}</strong>
                              <small>Acessar página no sistema</small>
                            </div>
                          </button>
                        )}

                        {/* Links adicionais */}
                        {response.additionalLinks && response.additionalLinks.length > 0 && (
                          <>
                            {response.additionalLinks.map((link, index) => (
                              <button
                                key={index}
                                className={`ai-action-btn ${getButtonClass(link.type, link.label)}`}
                                onClick={() => {
                                  if (link.type === 'link' && link.url) {
                                    handleOpenLink(link.url);
                                  } else if (link.type === 'contact' && link.whatsappUrl) {
                                    handleOpenLink(link.whatsappUrl);
                                  }
                                }}
                              >
                                {getIconForLink(link.type, link.label)}
                                <div>
                                  <strong>{link.label}</strong>
                                  {link.type === 'link' && <small>Abrir em nova aba</small>}
                                  {link.type === 'contact' && (
                                    <small>
                                      WhatsApp {isMobile ? 'Mobile' : 'Web'}
                                    </small>
                                  )}
                                </div>
                              </button>
                            ))}
                          </>
                        )}

                        {/* WhatsApp de suporte */}
                        <button
                          className="ai-action-btn whatsapp secondary"
                          onClick={() => handleOpenLink(response.whatsappUrl)}
                        >
                          <Phone size={20} />
                          <div>
                            <strong>Precisa de mais ajuda?</strong>
                            <small>
                              Falar com {response.department?.toUpperCase() || 'especialista'} {isMobile ? '📱' : '💻'}
                            </small>
                          </div>
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Não encontrado */}
                      <div className="ai-notfound-message">
                        <Bot size={20} />
                        <div>
                          <strong>Informação não encontrada na base</strong>
                          <p>{response.message}</p>
                        </div>
                      </div>

                      <div className="ai-response-actions">
                        <button
                          className="ai-action-btn whatsapp"
                          onClick={() => handleOpenLink(response.whatsappUrl)}
                        >
                          <MessageCircle size={20} />
                          <div>
                            <strong>Falar com especialista no WhatsApp</strong>
                            <small>
                              Obter resposta personalizada de {response.department?.toUpperCase() || 'nossa equipe'} via WhatsApp {isMobile ? 'Mobile' : 'Web'}
                            </small>
                          </div>
                        </button>
                      </div>
                    </>
                  )}

                  {/* Botão para nova pergunta */}
                  <button className="ai-new-question" onClick={resetSearch}>
                    <Search size={16} />
                    Fazer nova pergunta
                  </button>

                  {/* Debug info */}
                  {debug && (
                    <div className="ai-debug-info">
                      <small>
                        System: {flow?.url?.toUpperCase()} | 
                        Confidence: {response.confidence?.toFixed(2)} | 
                        Department: {response.department} | 
                        Device: {isMobile ? 'Mobile' : 'Desktop'}
                      </small>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AISearchBox;